/**
 * **Undo, built out of a delay rather than out of a withdrawal.**
 *
 * `POST /attention-queue/responses` has no inverse. There is no endpoint that takes a response back
 * and none that lists snoozed items, so a response sent is a response the trainer cannot undo — and
 * a screen that clears fourteen items by keyboard needs an undo more than any other in this app.
 *
 * The answer is to HOLD the last decision for about five seconds before writing it. The card is
 * already struck through in the index, the next card is already selected, and nothing about the
 * screen waits — but the request has not gone. `Z` cancels it and the card comes back, having never
 * left the trainer's list. Making the next decision, leaving the screen, or the tab going away all
 * RELEASE it at once, so the hold is never a way to lose work.
 *
 * Only ONE decision is ever held, single or bulk. A stack of pending writes would mean a trainer
 * could lose a minute of work to a closed tab, and "undo" would stop meaning "the thing I just
 * did".
 *
 * Pure: no React, no DOM, no timers, no query hook. The clock and the `pagehide` listener are
 * `lib/useHeldResponses`'; every rule about WHAT happens is here.
 */

import type { HandledKind } from './taskDeck';

/** How long a decision waits before it is written. */
export const HOLD_MS = 5000;

/** One decision the trainer made: an act, the cards it covers, and when a snooze would return. */
export interface HeldDecision {
  readonly kind: HandledKind;
  /** The cards this decision answers. One for a single press, N for a bulk act. */
  readonly keys: readonly string[];
  /** The instant a snooze names, or `null` for a "gotowe". R6 requires a future one. */
  readonly snoozeUntilIso: string | null;
}

/** What is waiting to be written, or nothing. */
export interface HeldState {
  readonly held: HeldDecision | null;
}

/** Nothing held. The state a screen mounts in. */
export const NOTHING_HELD: HeldState = { held: null };

/**
 * What a transition asks the caller to do: a decision to WRITE now, and the new state.
 *
 * `write` is `null` whenever nothing has to reach the server, which is what makes "undo sends
 * nothing" a property of this module rather than of the hook.
 */
export interface HeldTransition {
  readonly write: HeldDecision | null;
  readonly state: HeldState;
}

/**
 * Take a new decision.
 *
 * The one already held is written FIRST — the trainer has moved on, so its five seconds are over —
 * and the new one takes its place. That is the whole reason a held decision never queues behind
 * another: there is only ever one, and it is always the last thing the trainer did.
 */
export function holdDecision(state: HeldState, decision: HeldDecision): HeldTransition {
  return { write: state.held, state: { held: decision } };
}

/**
 * Cancel what is held.
 *
 * Nothing is written and nothing is sent — the point of the hold. What comes back is the decision
 * that was cancelled, so the caller can return its cards to the deck and say so out loud; `null`
 * means the trainer pressed undo with nothing to undo, which is a fact the surface reports rather
 * than a no-op it swallows.
 */
export function cancelHeld(state: HeldState): { readonly cancelled: HeldDecision | null; readonly state: HeldState } {
  return { cancelled: state.held, state: NOTHING_HELD };
}

/**
 * Write what is held, now.
 *
 * The hold's timer firing, the screen unmounting and `pagehide` all land here, and all three mean
 * the same thing: the trainer's five seconds are over or their chance to use them is. Releasing an
 * empty state is a no-op, which is what makes it safe to call from an unmount that races the timer.
 */
export function releaseHeld(state: HeldState): HeldTransition {
  return { write: state.held, state: NOTHING_HELD };
}

/** Is this card's answer still recallable? The index's "Cofnij" and the `Z` legend read it. */
export function heldCovers(state: HeldState, key: string): boolean {
  return state.held?.keys.includes(key) ?? false;
}
